import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import {
  List,
  Button as RSuiteButton,
  ButtonGroup as RSuiteButtonGroup,
  useToaster,
  Message,
  Tooltip,
  Whisper,
  InputGroup,
  Toggle,
} from "rsuite";
import RemindFillIcon from "@rsuite/icons/RemindFill";
import InfoRoundIcon from "@rsuite/icons/InfoRound";
import SearchIcon from "@rsuite/icons/Search";
import styled from "styled-components";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import {
  SectionRibbon,
  SpacedGlassCard,
  Picker,
  Button,
  ButtonGroup,
  Loader,
  NoDataState,
  GroupedSitePicker,
  Modal,
} from "../../../../ui";
import { colors } from "../../../../ui/theme/colors";
import { shadows } from "../../../../ui/theme/shadows";

export const AccessListFC = (props) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [baseCustomers, setBaseCustomers] = useState([]);
  const [displayList, setDisplayList] = useState([]);
  const [userDataModel, setUserDataModel] = useState([]);
  const [userSystemListConfig, setUserSystemListConfig] = useState([]);
  const [loadingUserConfig, setLoadingUserConfig] = useState(true);
  const [userCustomers, setUserCustomers] = useState([]);
  const [userSites, setUserSites] = useState([]);
  const [disableButton, setDisableButton] = useState(false);
  const [userManufacturers, setUserManufacturer] = useState([]);
  const [userModalities, setUserModalities] = useState([]);

  // Site Groups state - separate for each customer
  const [customerSiteGroups, setCustomerSiteGroups] = useState({});
  const [userSiteGroupsByCustomer, setUserSiteGroupsByCustomer] = useState({});
  const [loadingSiteGroups, setLoadingSiteGroups] = useState({});

  // Per-customer "All sites" toggle state. true = implicit access to every site
  // (including future ones); false = restricted to picked sites/groups.
  const [allSitesModeByCustomer, setAllSitesModeByCustomer] = useState({});
  const [prefillComplete, setPrefillComplete] = useState(false);
  const [confirmAllSitesCustomerId, setConfirmAllSitesCustomerId] = useState(null);


  const [resMsg, setResMsg] = useState("");
  const toaster = useToaster();



  // Memoized helper function to check if a customer has any assignments
  const customerHasAssignments = useCallback(
    (customerId) => {
      // Check for individual sites
      const hasIndividualSites = userSites.some(
        (site) => site[0] === customerId && site[1] !== "none",
      );

      // Check for site groups
      const hasSiteGroups =
        userSiteGroupsByCustomer[customerId] &&
        userSiteGroupsByCustomer[customerId].length > 0;

      // Check for manufacturers
      const hasManufacturers = userManufacturers.some(
        (manufacturer) => manufacturer[0] === customerId,
      );

      // Check for modalities
      const hasModalities = userModalities.some(
        (modality) => modality[0] === customerId,
      );

      return (
        hasIndividualSites || hasSiteGroups || hasManufacturers || hasModalities
      );
    },
    [userSites, userSiteGroupsByCustomer, userManufacturers, userModalities],
  );

  // Memoized helper function to get assignment details for tooltip
  const getAssignmentDetails = useCallback(
    (customerId) => {
      const assignments = [];

      const individualSites = userSites.filter(
        (site) => site[0] === customerId && site[1] !== "none",
      );
      if (individualSites.length > 0) {
        assignments.push(
          `${individualSites.length} individual site${individualSites.length > 1 ? "s" : ""}`,
        );
      }

      const siteGroups = userSiteGroupsByCustomer[customerId] || [];
      if (siteGroups.length > 0) {
        assignments.push(
          `${siteGroups.length} site group${siteGroups.length > 1 ? "s" : ""}`,
        );
      }

      const manufacturers = userManufacturers.filter(
        (manufacturer) => manufacturer[0] === customerId,
      );
      if (manufacturers.length > 0) {
        assignments.push(
          `${manufacturers.length} manufacturer${manufacturers.length > 1 ? "s" : ""}`,
        );
      }

      const modalities = userModalities.filter(
        (modality) => modality[0] === customerId,
      );
      if (modalities.length > 0) {
        assignments.push(
          `${modalities.length} ${modalities.length > 1 ? "modalities" : "modality"}`,
        );
      }

      return assignments;
    },
    [userSites, userSiteGroupsByCustomer, userManufacturers, userModalities],
  );

  const handleChangeCustomers = (value) => {
    const removedCustomers = userCustomers.filter((id) => !value.includes(id));

    // Check if any removed customers have assignments
    const customersWithAssignments = removedCustomers.filter((customerId) =>
      customerHasAssignments(customerId),
    );

    if (customersWithAssignments.length > 0) {
      // Prevent removal and show warning
      const customerNames = customersWithAssignments.map((customerId) => {
        const customer = baseCustomers.find((c) => c.value === customerId);
        return customer ? customer.label : `Customer ${customerId}`;
      });

      const assignmentDetails = customersWithAssignments.map((customerId) => {
        const customer = baseCustomers.find((c) => c.value === customerId);
        const customerName = customer
          ? customer.label
          : `Customer ${customerId}`;
        const details = getAssignmentDetails(customerId);
        return `${customerName}: ${details.join(", ")}`;
      });

      const warningMessage = (
        <Message showIcon type="warning" header="Cannot Remove Customer">
          <div>
            <p>Please remove all assignments first:</p>
            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
              {assignmentDetails.map((detail, index) => (
                <li key={index} style={{ marginBottom: "4px" }}>
                  {detail}
                </li>
              ))}
            </ul>
          </div>
        </Message>
      );

      toaster.push(warningMessage, {
        placement: "topCenter",
        duration: 5000,
      });

      return; // Don't update the customer selection
    }

    setUserCustomers(value);
  };

  const successMessage = (message) => (
    <Message showIcon type="success" header="Success">
      {message}
    </Message>
  );

  const errorMessage = (message = "Failed to update. Please try again.") => (
    <Message showIcon type="error" header="Error">
      {message}
    </Message>
  );

  const warningMessage = (
    <Message showIcon type="warning" header="Warning">
      A User must have at least (1) Customer selected within their Access List.
    </Message>
  );

  // Optimized batch loading of site groups for multiple customers
  const loadSiteGroupsForCustomers = useCallback(
    async (customerIds) => {
      const customersToLoad = customerIds.filter(
        (id) => !customerSiteGroups[id],
      );
      if (customersToLoad.length === 0) return;

      // Set loading state for all customers being loaded
      setLoadingSiteGroups((prev) => {
        const newState = { ...prev };
        customersToLoad.forEach((id) => (newState[id] = true));
        return newState;
      });

      try {
        const token = await getAccessTokenSilently({
          audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
        });

        // Batch all API calls — fetch both groups and their site mappings
        const promises = customersToLoad.map(async (customerId) => {
          try {
            const [groupsResponse, mappingsResponse] = await Promise.all([
              axiosInstance({
                url: `/customer/site-groups/${customerId}`,
                headers: { authorization: `Bearer ${token}` },
              }),
              axiosInstance({
                url: `/customer/site-group-mappings/${customerId}`,
                headers: { authorization: `Bearer ${token}` },
              }),
            ]);
            const siteGroups =
              groupsResponse.data[0] || groupsResponse.data || [];
            const mappings =
              mappingsResponse.data[0] || mappingsResponse.data || [];

            // Build a map of groupId -> sites from the mappings
            const sitesPerGroup = {};
            mappings.forEach((mapping) => {
              if (!sitesPerGroup[mapping.site_group_id]) {
                sitesPerGroup[mapping.site_group_id] = [];
              }
              sitesPerGroup[mapping.site_group_id].push({
                site_id: mapping.site_id,
                site_name: mapping.site_name,
              });
            });

            return {
              customerId,
              siteGroups: siteGroups.map((group) => ({
                value: group.id,
                label: group.name,
                sites: sitesPerGroup[group.id] || [],
              })),
            };
          } catch (error) {
            console.error(
              `Error loading site groups for customer ${customerId}:`,
              error,
            );
            return { customerId, siteGroups: [] };
          }
        });

        const results = await Promise.all(promises);

        // Update state with all results at once
        setCustomerSiteGroups((prev) => {
          const newState = { ...prev };
          results.forEach(({ customerId, siteGroups }) => {
            newState[customerId] = siteGroups;
          });
          return newState;
        });
      } catch (error) {
        console.error("Error in batch loading site groups:", error);
      } finally {
        // Clear loading state for all customers
        setLoadingSiteGroups((prev) => {
          const newState = { ...prev };
          customersToLoad.forEach((id) => (newState[id] = false));
          return newState;
        });
      }
    },
    [customerSiteGroups, getAccessTokenSilently],
  );

  // Optimized batch loading of user site groups for multiple customers
  const loadUserSiteGroupsForCustomers = useCallback(
    async (customerIds) => {
      const customersToLoad = customerIds.filter(
        (id) => !userSiteGroupsByCustomer[id],
      );
      if (customersToLoad.length === 0) return;

      try {
        const token = await getAccessTokenSilently({
          audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
        });

        // Batch all API calls
        const promises = customersToLoad.map(async (customerId) => {
          try {
            const response = await axiosInstance({
              url: `/admin/user-site-groups/${props.user}/${customerId}`,
              headers: { authorization: `Bearer ${token}` },
            });
            const assignedGroups = (response.data || []).map(
              (group) => group.id,
            );
            return { customerId, assignedGroups };
          } catch (error) {
            console.error(
              `Error loading user site groups for customer ${customerId}:`,
              error,
            );
            return { customerId, assignedGroups: [] };
          }
        });

        const results = await Promise.all(promises);

        // Update state with all results at once
        setUserSiteGroupsByCustomer((prev) => {
          const newState = { ...prev };
          results.forEach(({ customerId, assignedGroups }) => {
            newState[customerId] = assignedGroups;
          });
          return newState;
        });
      } catch (error) {
        console.error("Error in batch loading user site groups:", error);
      }
    },
    [userSiteGroupsByCustomer, props.user, getAccessTokenSilently],
  );

  // Helper function to resolve site groups to individual site IDs
  const resolveSiteGroupsToSites = async (customerId, siteGroupIds) => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      // Get site group mappings for this customer
      const response = await axiosInstance({
        url: `/customer/site-group-mappings/${customerId}`,
        headers: { authorization: `Bearer ${token}` },
      });

      const siteGroupMappings = response.data[0] || response.data || [];

      // Get sites that belong to the specified site groups
      let siteIds = [];
      siteGroupIds.forEach((groupId) => {
        const siteIdsInGroup = siteGroupMappings
          .filter((mapping) => mapping.site_group_id === groupId)
          .map((mapping) => mapping.site_id);
        siteIds.push(...siteIdsInGroup);
      });

      // Remove duplicates
      return [...new Set(siteIds)];
    } catch (error) {
      console.error(
        `Error resolving site groups for customer ${customerId}:`,
        error,
      );
      return [];
    }
  };

  const getUserSystemListConfig = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const systemConfigResponse = await axiosInstance({
        url: `/admin/user/system-list-config/${props.user}`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setUserSystemListConfig(systemConfigResponse.data[0] || []);
    } catch (error) {
      console.error("Error loading user system config:", error);
      setUserSystemListConfig([]);
    }
    setLoadingUserConfig(false);
  };

  const setBaseList = () => {
    // Set Admin List to Interact with User
    const customers = [
      ...new Map(
        props.baseSystems.map((item) => [
          item["customer_id"],
          {
            value: item["customer_id"],
            label: item["customer_name"],
            hasAssignments: false, // Will be updated dynamically
            assignmentDetails: [],
          },
        ]),
      ).values(),
    ];
    setBaseCustomers(customers);
  };

  // Memoized enhanced customers to prevent recalculation on every render
  const enhancedCustomers = useMemo(() => {
    return baseCustomers.map((customer) => ({
      ...customer,
      hasAssignments: customerHasAssignments(customer.value),
      assignmentDetails: getAssignmentDetails(customer.value),
    }));
  }, [baseCustomers, customerHasAssignments, getAssignmentDetails]);

  // Deduplicated sites per customer for the GroupedSitePicker
  const allSitesByCustomer = useMemo(() => {
    const map = {};
    props.baseSystems.forEach((system) => {
      if (!map[system.customer_id]) map[system.customer_id] = new Map();
      if (!map[system.customer_id].has(system.site_id)) {
        map[system.customer_id].set(system.site_id, {
          value: [system.customer_id, system.site_id],
          label: system.site_name,
          site_id: system.site_id,
        });
      }
    });
    const result = {};
    for (const [custId, siteMap] of Object.entries(map)) {
      result[custId] = Array.from(siteMap.values());
    }
    return result;
  }, [props.baseSystems]);

  const handleButton = () => {
    if (userCustomers.length === 0 && !loadingUserConfig) {
      setDisableButton(true);
      toaster.push(warningMessage, { placement: "topCenter", duration: 3000 });
    } else {
      setDisableButton(false);
    }
  };

  const handleGroupSelectionChange = useCallback((customerId, groupIds) => {
    setUserSiteGroupsByCustomer((prev) => ({
      ...prev,
      [customerId]: groupIds,
    }));
  }, []);

  const handleIndividualSiteSelectionChange = useCallback(
    (customerId, sites) => {
      setUserSites((prev) => [
        ...prev.filter(([custId]) => custId !== customerId),
        ...sites,
      ]);
    },
    [],
  );

  // After prefill finishes, default each customer's mode based on whether
  // there are existing selections. Customers without any picked sites/groups
  // start in "All sites" mode (matches existing implicit-access semantics).
  useEffect(() => {
    if (!prefillComplete) return;
    const modeMap = {};
    userCustomers.forEach((cid) => {
      const hasSites = userSites.some(
        (site) => site[0] === cid && site[1] !== "none",
      );
      const hasGroups = (userSiteGroupsByCustomer[cid] || []).length > 0;
      modeMap[cid] = !(hasSites || hasGroups);
    });
    setAllSitesModeByCustomer(modeMap);
    // Intentionally only triggered by prefillComplete — userSites/userCustomers
    // changes during editing must NOT reset the toggle state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillComplete]);

  const handleAllSitesToggle = useCallback(
    (customerId, checked) => {
      if (checked) {
        // Switching ON. If selections exist, ask before clearing them.
        const hasSites = userSites.some(
          (site) => site[0] === customerId && site[1] !== "none",
        );
        const hasGroups =
          (userSiteGroupsByCustomer[customerId] || []).length > 0;
        if (hasSites || hasGroups) {
          setConfirmAllSitesCustomerId(customerId);
          return;
        }
      }
      setAllSitesModeByCustomer((prev) => ({ ...prev, [customerId]: checked }));
    },
    [userSites, userSiteGroupsByCustomer],
  );

  const confirmAllSitesSwitch = useCallback(() => {
    const cid = confirmAllSitesCustomerId;
    if (cid === null) return;
    setUserSites((prev) => prev.filter(([c]) => c !== cid));
    setUserSiteGroupsByCustomer((prev) => ({ ...prev, [cid]: [] }));
    setAllSitesModeByCustomer((prev) => ({ ...prev, [cid]: true }));
    setConfirmAllSitesCustomerId(null);
  }, [confirmAllSitesCustomerId]);

  // Optimized batch filtering of site group sites
  const filterOutSiteGroupSites = useCallback(
    async (allSites) => {
      try {
        const individualSites = [];

        // Group sites by customer
        const sitesByCustomer = {};
        allSites.forEach(([customerId, siteId]) => {
          if (!sitesByCustomer[customerId]) {
            sitesByCustomer[customerId] = [];
          }
          sitesByCustomer[customerId].push(siteId);
        });

        const token = await getAccessTokenSilently({
          audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
        });

        const customerIds = Object.keys(sitesByCustomer);

        // Batch load site group mappings for all customers at once
        const mappingPromises = customerIds.map(async (customerId) => {
          const assignedSiteGroups = userSiteGroupsByCustomer[customerId] || [];

          if (assignedSiteGroups.length === 0) {
            // No site groups, all sites are individual
            return {
              customerId,
              individualSiteIds: sitesByCustomer[customerId],
            };
          }

          try {
            const response = await axiosInstance({
              url: `/customer/site-group-mappings/${customerId}`,
              headers: { authorization: `Bearer ${token}` },
            });

            const siteGroupMappings = response.data[0] || response.data || [];

            // Get sites from assigned site groups
            const siteGroupSites = assignedSiteGroups.flatMap((groupId) =>
              siteGroupMappings
                .filter((mapping) => mapping.site_group_id === groupId)
                .map((mapping) => mapping.site_id),
            );

            const uniqueSiteGroupSites = [...new Set(siteGroupSites)];

            // Filter out site group sites from customer sites
            const customerIndividualSites = sitesByCustomer[customerId].filter(
              (siteId) => !uniqueSiteGroupSites.includes(siteId),
            );

            return {
              customerId,
              individualSiteIds: customerIndividualSites,
            };
          } catch (error) {
            console.error(
              `Error fetching site group mappings for customer ${customerId}:`,
              error,
            );
            return {
              customerId,
              individualSiteIds: sitesByCustomer[customerId], // Return all sites if error
            };
          }
        });

        // Wait for all mappings to resolve
        const results = await Promise.all(mappingPromises);

        // Collect all individual sites
        results.forEach(({ customerId, individualSiteIds }) => {
          individualSiteIds.forEach((siteId) => {
            individualSites.push([customerId, siteId]);
          });
        });

        return individualSites;
      } catch (error) {
        console.error("Error filtering site group sites:", error);
        return allSites;
      }
    },
    [userSiteGroupsByCustomer, getAccessTokenSilently],
  );

  const preFillUserOptions = async () => {
    // Reset mode state and gate the init effect until prefill finishes,
    // otherwise it would evaluate against half-loaded data (site groups
    // populate before individual sites).
    setPrefillComplete(false);
    setAllSitesModeByCustomer({});

    // console.log("DEBUG: preFillUserOptions called");
    // Prefill Current Selected User Customer List
    let configCustomer = [];
    let configSites = [];
    let configManufacturers = [];
    let configModalities = [];

    // console.log(
    //   "DEBUG: userSystemListConfig in preFillUserOptions:",
    //   userSystemListConfig,
    // );

    try {
      userSystemListConfig.forEach((config) => {
        // Filter out null/undefined customer IDs
        if (
          config.customers__id !== null &&
          config.customers__id !== undefined
        ) {
          configCustomer.push(JSON.parse(JSON.stringify(config.customers__id)));
        }

        if (config.and?.sites__id) {
          config.and?.sites__id.forEach((site) => {
            // Only add sites for valid customers
            if (
              config.customers__id !== null &&
              config.customers__id !== undefined
            ) {
              configSites.push([
                JSON.parse(JSON.stringify(config.customers__id)),
                site,
              ]);
            }
          });
        }

        if (config.and?.systems__manufacturer) {
          config.and?.systems__manufacturer.forEach((manufacturer) => {
            configManufacturers.push([
              JSON.parse(JSON.stringify(config.customers__id)),
              manufacturer,
            ]);
          });
        } else if (config.and?.and?.systems__manufacturer) {
          config.and?.and?.systems__manufacturer.forEach((manufacturer) => {
            configManufacturers.push([
              JSON.parse(JSON.stringify(config.customers__id)),
              manufacturer,
            ]);
          });
        } else if (config.and?.and?.and?.systems__manufacturer) {
          config.and?.and?.and?.systems__manufacturer.forEach(
            (manufacturer) => {
              configManufacturers.push([
                JSON.parse(JSON.stringify(config.customers__id)),
                manufacturer,
              ]);
            },
          );
        }

        if (config.and?.systems__modality) {
          config.and?.systems__modality.forEach((modality) => {
            configModalities.push([
              JSON.parse(JSON.stringify(config.customers__id)),
              modality,
            ]);
          });
        } else if (config.and?.and?.systems__modality) {
          config.and?.and?.systems__modality.forEach((modality) => {
            configModalities.push([
              JSON.parse(JSON.stringify(config.customers__id)),
              modality,
            ]);
          });
        } else if (config.and?.and?.and?.systems__modality) {
          config.and?.and?.and?.systems__modality.forEach((modality) => {
            configModalities.push([
              JSON.parse(JSON.stringify(config.customers__id)),
              modality,
            ]);
          });
        }
      });
    } catch {
      console.log("INVALID: Config");
    }

    // console.log(
    //   "DEBUG: configCustomer before setUserCustomers:",
    //   configCustomer,
    // );
    // console.log("DEBUG: configSites before filtering:", configSites);

    // Set basic customer and site data first
    setUserCustomers(configCustomer);
    setUserModalities(configModalities);
    setUserManufacturer(configManufacturers);

    // Batch load site groups and user site groups for all customers
    await Promise.all([
      loadSiteGroupsForCustomers(configCustomer),
      loadUserSiteGroupsForCustomers(configCustomer),
    ]);

    // Filter sites to separate individual sites from site group sites
    const individualSites = await filterOutSiteGroupSites(configSites);

    setUserSites(individualSites);
    setPrefillComplete(true);
  };

  const onSubmitGenerateUserAlerts = async (e) => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/alert/generate/model`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          user_id: props.user.toString(),
        }),
      });
    } catch (error) {
      console.error(error);
    }
  };

  const onSubmit = async () => {
    try {
      setLoadingUserConfig(true);
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      // Create unified data model with all sites resolved from both individual and site groups
      let unifiedDataModel = [];

      for (const customer of userCustomers) {
        let userDataConfig = {};
        let userSiteConfig = [];
        let userModalityConfig = [];
        let userManufacturerConfig = [];

        userDataConfig.customers__id = customer;

        // Collect individual sites (excluding "none")
        if (userSites.length > 0) {
          userSites.forEach((site) => {
            if (site[0] === customer && site[1] !== "none") {
              userSiteConfig.push(site[1]);
            }
          });
        }

        // Resolve site groups to individual site IDs and add them
        const userSiteGroups = userSiteGroupsByCustomer[customer] || [];
        if (userSiteGroups.length > 0) {
          const siteGroupSites = await resolveSiteGroupsToSites(
            customer,
            userSiteGroups,
          );
          userSiteConfig.push(...siteGroupSites);
        }

        // Remove duplicates from combined site list
        userSiteConfig = [...new Set(userSiteConfig)];

        // Collect manufacturers
        if (userManufacturers.length > 0) {
          userManufacturers.forEach((manufacturer) => {
            if (manufacturer[0] === customer) {
              userManufacturerConfig.push(manufacturer[1]);
            }
          });
        }

        // Collect modalities
        if (userModalities.length > 0) {
          userModalities.forEach((modalities) => {
            if (modalities[0] === customer) {
              userModalityConfig.push(modalities[1]);
            }
          });
        }

        // Build configuration object with proper nested structure
        let hasConstraints = false;

        if (userSiteConfig.length > 0) {
          if (!userDataConfig.and) userDataConfig.and = {};
          userDataConfig.and.sites__id = userSiteConfig;
          hasConstraints = true;
        }

        if (userManufacturerConfig.length > 0) {
          if (!userDataConfig.and) userDataConfig.and = {};
          userDataConfig.and.systems__manufacturer = userManufacturerConfig;
          hasConstraints = true;
        }

        if (userModalityConfig.length > 0) {
          if (!userDataConfig.and) userDataConfig.and = {};
          userDataConfig.and.systems__modality = userModalityConfig;
          hasConstraints = true;
        }

        // Only add customer config if it has constraints or customer is selected
        if (hasConstraints || userCustomers.includes(customer)) {
          unifiedDataModel.push(JSON.parse(JSON.stringify(userDataConfig)));
        }
      }

      console.log(
        "Using simplified unified API (site groups resolved to individual sites):",
        {
          unifiedDataModel,
          userEmail: props.user,
          userCustomers,
          userSites,
          userManufacturers,
          userModalities,
          userSiteGroupsByCustomer, // For debugging - shows what site groups were resolved
        },
      );

      // Additional debugging - log each configuration object structure
      unifiedDataModel.forEach((config, index) => {
        console.log(`CONFIG ${index}:`, {
          customers__id: config.customers__id,
          hasAnd: !!config.and,
          andKeys: config.and ? Object.keys(config.and) : null,
          andValues: config.and ? Object.values(config.and) : null,
          fullConfig: JSON.stringify(config, null, 2),
        });
      });

      // Single unified API call - no site groups needed since they're resolved to individual sites
      await axiosInstance({
        method: "POST",
        url: `/admin/update/user-access-unified/`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          updateUser: props.user,
          userModel: unifiedDataModel,
          userSiteGroupsByCustomer: userSiteGroupsByCustomer, // Pass site groups separately
          created_by: user?.email,
          last_updated_by: user?.email,
        }),
      });

      console.log(
        "✅ Successfully updated all user access via simplified unified API",
      );

      setResMsg("Access updated successfully");
      onSubmitGenerateUserAlerts();
      toaster.push(successMessage("Access updated successfully"), {
        placement: "topCenter",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error updating access:", error);
      toaster.push(errorMessage("Failed to update access. Please try again."), {
        placement: "topCenter",
        duration: 3000,
      });
    }
    setLoadingUserConfig(false);
  };

  useEffect(() => {
    getUserSystemListConfig();
    setBaseList();
  }, [props.user, props.baseSystems]);

  // Only run preFillUserOptions once when userSystemListConfig is first loaded
  const hasLoadedUserOptions = React.useRef(false);

  useEffect(() => {
    const loadUserOptions = async () => {
      if (userSystemListConfig.length > 0 && !hasLoadedUserOptions.current) {
        hasLoadedUserOptions.current = true;
        await preFillUserOptions();
      }
    };
    loadUserOptions();
  }, [userSystemListConfig.length]);


  const setDisplayTable = useCallback(async () => {
    console.log("DEBUG: setDisplayTable called");
    const displayCustomers = baseCustomers?.filter((customer) => {
      return userCustomers?.includes(customer.value);
    });
    setDisplayList(displayCustomers);

    if (!displayCustomers?.length) return;

    const customerIds = displayCustomers.map((customer) => customer.value);

    // Batch load site groups and user site groups for all displayed customers
    await Promise.all([
      loadSiteGroupsForCustomers(customerIds),
      loadUserSiteGroupsForCustomers(customerIds),
    ]);
  }, [
    baseCustomers,
    userCustomers,
    loadSiteGroupsForCustomers,
    loadUserSiteGroupsForCustomers,
  ]);

  // Memoize the data model creation to avoid unnecessary recalculations
  const memoizedDataModel = useMemo(() => {
    let userCustomerConfig = [];

    for (const customer of userCustomers) {
      let userDataConfig = {};
      let userSiteConfig = [];
      let userModalityConfig = [];
      let userManufacturerConfig = [];

      userDataConfig.customers__id = customer;

      if (userSites.length > 0) {
        userSites.forEach((site) => {
          if (site[0] === customer) {
            userSiteConfig.push(site[1]);
          }
        });
      }

      if (userManufacturers.length > 0) {
        userManufacturers.forEach((manufacturer) => {
          if (manufacturer[0] === customer) {
            userManufacturerConfig.push(manufacturer[1]);
          }
        });
      }

      if (userModalities.length > 0) {
        userModalities.forEach((modalities) => {
          if (modalities[0] === customer) {
            userModalityConfig.push(modalities[1]);
          }
        });
      }

      if (userSiteConfig.length > 0) {
        if (userDataConfig.and) {
          if (userDataConfig.and.and) {
            userDataConfig.and.and.and = { sites__id: userSiteConfig };
          } else {
            userDataConfig.and.and = { sites__id: userSiteConfig };
          }
        } else {
          userDataConfig.and = { sites__id: userSiteConfig };
        }
      }

      if (userManufacturerConfig.length > 0) {
        if (userDataConfig.and) {
          if (userDataConfig.and.and) {
            userDataConfig.and.and.and = {
              systems__manufacturer: userManufacturerConfig,
            };
          } else {
            userDataConfig.and.and = {
              systems__manufacturer: userManufacturerConfig,
            };
          }
        } else {
          userDataConfig.and = {
            systems__manufacturer: userManufacturerConfig,
          };
        }
      }

      if (userModalityConfig.length > 0) {
        if (userDataConfig.and) {
          if (userDataConfig.and.and) {
            userDataConfig.and.and.and = {
              systems__modality: userModalityConfig,
            };
          } else {
            userDataConfig.and.and = { systems__modality: userModalityConfig };
          }
        } else {
          userDataConfig.and = { systems__modality: userModalityConfig };
        }
      }

      userCustomerConfig.push(JSON.parse(JSON.stringify(userDataConfig)));
    }

    return userCustomerConfig;
  }, [userCustomers, userSites, userManufacturers, userModalities]);

  // Update data model when memoized version changes
  useEffect(() => {
    setUserDataModel(memoizedDataModel);
  }, [memoizedDataModel]);

  // Separate effect for display table updates (less frequent triggers)
  useEffect(() => {
    setDisplayTable();
  }, [userCustomers.length, baseCustomers.length]);

  // Separate effect for button state
  useEffect(() => {
    handleButton();
  }, [userCustomers, loadingUserConfig]);

  // Memoize manufacturer data by customer to avoid recalculation on every render
  const manufacturerDataByCustomer = useMemo(() => {
    const dataMap = {};
    displayList?.forEach((customer) => {
      dataMap[customer.value] = [
        ...new Map(
          props.baseSystems
            .filter((system) => system.customer_id === customer.value)
            .map((item) => {
              // Extract manufacturer name without customer ID prefix
              let manufacturerName = item["manufacturer"];
              // Remove customer ID prefix if present (e.g., "123_GE Healthcare" -> "GE Healthcare")
              if (manufacturerName && manufacturerName.includes("_")) {
                const parts = manufacturerName.split("_");
                // Check if first part is numeric (customer ID)
                if (!isNaN(parts[0])) {
                  manufacturerName = parts.slice(1).join("_");
                }
              }

              return [
                item["manufacturer"],
                {
                  value: [item["customer_id"], item["manufacturer"]],
                  label: manufacturerName,
                },
              ];
            }),
        ).values(),
      ];
    });
    return dataMap;
  }, [displayList, props.baseSystems]);

  // Memoize modality data by customer to avoid recalculation on every render
  const modalityDataByCustomer = useMemo(() => {
    const dataMap = {};
    displayList?.forEach((customer) => {
      dataMap[customer.value] = [
        ...new Map(
          props.baseSystems
            .filter((system) => system.customer_id === customer.value)
            .map((item) => {
              // Extract modality name without customer ID prefix
              let modalityName = item["modality"];
              // Remove customer ID prefix if present (e.g., "123_CT" -> "CT")
              if (modalityName && modalityName.includes("_")) {
                const parts = modalityName.split("_");
                // Check if first part is numeric (customer ID)
                if (!isNaN(parts[0])) {
                  modalityName = parts.slice(1).join("_");
                }
              }

              return [
                item["modality"],
                {
                  value: [item["customer_id"], item["modality"]],
                  label: modalityName,
                },
              ];
            }),
        ).values(),
      ];
    });
    return dataMap;
  }, [displayList, props.baseSystems]);

  return (
    <div>
      <SectionRibbon title="Access List" />

      <SpacedGlassCard>
        <SectionTitle>Current Customer Access</SectionTitle>
        <Picker
          value={userCustomers}
          data={enhancedCustomers}
          onChange={handleChangeCustomers}
          multiple={true}
          searchable={true}
          placeholder="Select customers to grant access"
          renderMenuItem={(label, item) => {
            const hasAssignments = item.hasAssignments;
            const assignmentDetails = item.assignmentDetails;

            if (hasAssignments) {
              const tooltipContent = (
                <div style={{ maxWidth: "250px" }}>
                  <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                    Cannot remove - has assignments:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: "16px" }}>
                    {assignmentDetails.map((detail, index) => (
                      <li key={index} style={{ marginBottom: "2px" }}>
                        {detail}
                      </li>
                    ))}
                  </ul>
                  <div
                    style={{
                      fontStyle: "italic",
                      marginTop: "4px",
                      fontSize: "12px",
                    }}
                  >
                    Remove assignments first to unselect this customer
                  </div>
                </div>
              );

              return (
                <Whisper
                  trigger="hover"
                  placement="topStart"
                  speaker={<Tooltip>{tooltipContent}</Tooltip>}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <span>{label}</span>
                    <RemindFillIcon
                      style={{
                        color: "#ff8f00",
                        fontSize: "14px",
                        marginLeft: "8px",
                      }}
                    />
                  </div>
                </Whisper>
              );
            }

            return <span>{label}</span>;
          }}
          showCheckAll
        />
      </SpacedGlassCard>

      <SpacedGlassCard>
        {displayList?.length === 0 ? (
          <NoDataState
            icon="🏢"
            title="No Customers Found"
            message="No customers available for access assignment."
          />
        ) : (
          <AccessTableContainer>
            <TableHeader>
              <TableHeaderCell width="25%">Customer</TableHeaderCell>
              <TableHeaderCell width="35%" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                Site Access
                <Whisper
                  trigger="hover"
                  placement="top"
                  speaker={<Tooltip>Toggle &quot;All sites&quot; to grant access to every site for the customer, including any added later. Toggle off to pick specific sites or site groups.</Tooltip>}
                >
                  <InfoRoundIcon style={{ fontSize: "13px", cursor: "pointer", opacity: 0.6 }} />
                </Whisper>
              </TableHeaderCell>
              <TableHeaderCell width="20%" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                Manufacturer
                <Whisper
                  trigger="hover"
                  placement="top"
                  speaker={<Tooltip>If nothing is selected, all manufacturers are included by default.</Tooltip>}
                >
                  <InfoRoundIcon style={{ fontSize: "13px", cursor: "pointer", opacity: 0.6 }} />
                </Whisper>
              </TableHeaderCell>
              <TableHeaderCell width="20%" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                Modality
                <Whisper
                  trigger="hover"
                  placement="topEnd"
                  speaker={<Tooltip>If nothing is selected, all modalities are included by default.</Tooltip>}
                >
                  <InfoRoundIcon style={{ fontSize: "13px", cursor: "pointer", opacity: 0.6 }} />
                </Whisper>
              </TableHeaderCell>
            </TableHeader>

            {displayList?.map((customer, index) => (
              <MemoizedListItem
                customer={customer}
                index={index}
                key={customer.customer_id}
                userSites={userSites}
                userManufacturers={userManufacturers}
                userModalities={userModalities}
                customerSiteGroups={customerSiteGroups}
                userSiteGroupsByCustomer={userSiteGroupsByCustomer}
                loadingSiteGroups={loadingSiteGroups}
                allSitesByCustomer={allSitesByCustomer}
                handleGroupSelectionChange={handleGroupSelectionChange}
                handleIndividualSiteSelectionChange={handleIndividualSiteSelectionChange}
                setUserManufacturer={setUserManufacturer}
                setUserModalities={setUserModalities}
                manufacturerDataByCustomer={manufacturerDataByCustomer}
                modalityDataByCustomer={modalityDataByCustomer}
                allSitesMode={
                  prefillComplete &&
                  allSitesModeByCustomer[customer.value] !== false
                }
                onAllSitesToggle={handleAllSitesToggle}
              />
            ))}
          </AccessTableContainer>
        )}
      </SpacedGlassCard>

      <SpacedGlassCard>
        <ButtonContainer>
          <ButtonGroup>
            <Button variant="secondary" onClick={preFillUserOptions}>
              Reset
            </Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={disableButton}
              loading={loadingUserConfig}
            >
              Apply Changes
            </Button>
          </ButtonGroup>
        </ButtonContainer>
      </SpacedGlassCard>

      <Modal
        open={confirmAllSitesCustomerId !== null}
        onClose={() => setConfirmAllSitesCustomerId(null)}
        title="Switch to All Sites?"
        size="xs"
        footerActions={
          <>
            <Button
              variant="subtle"
              onClick={() => setConfirmAllSitesCustomerId(null)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmAllSitesSwitch}>
              Switch to All Sites
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          This will clear the currently selected sites and site groups for{" "}
          <strong>
            {baseCustomers.find((c) => c.value === confirmAllSitesCustomerId)
              ?.label || "this customer"}
          </strong>
          . The user will gain access to <strong>every site</strong> for this
          customer, including any sites added in the future.
        </p>
        <p style={{ marginTop: 12, marginBottom: 0, color: colors.neutral[600] }}>
          Toggle off again to pick specific sites or site groups instead.
        </p>
      </Modal>
    </div>
  );
};

// Memoized list item component to prevent unnecessary re-renders
const MemoizedListItem = memo(
  ({
    customer,
    index,
    userSites,
    userManufacturers,
    userModalities,
    customerSiteGroups,
    userSiteGroupsByCustomer,
    loadingSiteGroups,
    allSitesByCustomer,
    handleGroupSelectionChange,
    handleIndividualSiteSelectionChange,
    setUserManufacturer,
    setUserModalities,
    manufacturerDataByCustomer,
    modalityDataByCustomer,
    allSitesMode,
    onAllSitesToggle,
  }) => {
    return (
      <TableRow key={customer.customer_id}>
        <TableCell width="25%" alignTop>
          <CustomerLabel>{customer.label}</CustomerLabel>
        </TableCell>

        <TableCell width="35%" alignTop>
          <SiteAccessColumn>
            <ToggleRow>
              <Toggle
                size="sm"
                checked={allSitesMode}
                onChange={(checked) =>
                  onAllSitesToggle(customer.value, checked)
                }
              />
              <ToggleLabel>All sites</ToggleLabel>
              <Whisper
                trigger="hover"
                placement="top"
                speaker={
                  <Tooltip>
                    <strong>All Sites ON:</strong> grants access to every site
                    for this customer, including any added in the future.
                    <br />
                    <strong>All Sites OFF:</strong> restricts access to the
                    specific sites or site groups you pick — the user will not
                    gain access to new sites added later.
                  </Tooltip>
                }
              >
                <InfoRoundIcon
                  style={{
                    fontSize: "13px",
                    cursor: "pointer",
                    opacity: 0.6,
                  }}
                />
              </Whisper>
            </ToggleRow>
            {loadingSiteGroups[customer.value] ? (
              <LoaderContainer>
                <Loader />
              </LoaderContainer>
            ) : (
              <GroupedSitePicker
                siteGroups={customerSiteGroups[customer.value] || []}
                allSites={allSitesByCustomer[customer.value] || []}
                customerId={customer.value}
                selectedGroupIds={userSiteGroupsByCustomer[customer.value] || []}
                selectedIndividualSites={userSites.filter(
                  ([c]) => c === customer.value,
                )}
                onGroupSelectionChange={(groupIds) =>
                  handleGroupSelectionChange(customer.value, groupIds)
                }
                onIndividualSiteSelectionChange={(sites) =>
                  handleIndividualSiteSelectionChange(customer.value, sites)
                }
                placeholder={
                  allSitesMode
                    ? "All sites (toggle off to pick specific)"
                    : "Select sites or site groups"
                }
                disabled={allSitesMode}
              />
            )}
          </SiteAccessColumn>
        </TableCell>

        <TableCell width="20%" alignTop>
          <Picker
            placeholder="All manufacturers"
            value={userManufacturers.filter((m) => m[0] === customer.value)}
            data={manufacturerDataByCustomer[customer.value] || []}
            onChange={setUserManufacturer}
            multiple={true}
            searchable={true}
          />
        </TableCell>

        <TableCell width="20%" alignTop>
          <Picker
            placeholder="All modalities"
            value={userModalities.filter((m) => m[0] === customer.value)}
            data={modalityDataByCustomer[customer.value] || []}
            onChange={setUserModalities}
            multiple={true}
            searchable={true}
          />
        </TableCell>
      </TableRow>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    const customerId = prevProps.customer.value;
    return (
      prevProps.customer.value === nextProps.customer.value &&
      prevProps.customer.label === nextProps.customer.label &&
      prevProps.allSitesMode === nextProps.allSitesMode &&
      prevProps.loadingSiteGroups[customerId] ===
        nextProps.loadingSiteGroups[customerId] &&
      // Only check relevant data for this specific customer
      JSON.stringify(
        prevProps.userSites.filter((site) => site[0] === customerId),
      ) ===
        JSON.stringify(
          nextProps.userSites.filter((site) => site[0] === customerId),
        ) &&
      JSON.stringify(
        prevProps.userManufacturers.filter((m) => m[0] === customerId),
      ) ===
        JSON.stringify(
          nextProps.userManufacturers.filter((m) => m[0] === customerId),
        ) &&
      JSON.stringify(
        prevProps.userModalities.filter((m) => m[0] === customerId),
      ) ===
        JSON.stringify(
          nextProps.userModalities.filter((m) => m[0] === customerId),
        ) &&
      JSON.stringify(prevProps.customerSiteGroups[customerId]) ===
        JSON.stringify(nextProps.customerSiteGroups[customerId]) &&
      JSON.stringify(prevProps.userSiteGroupsByCustomer[customerId]) ===
        JSON.stringify(nextProps.userSiteGroupsByCustomer[customerId]) &&
      JSON.stringify(prevProps.allSitesByCustomer[customerId]) ===
        JSON.stringify(nextProps.allSitesByCustomer[customerId]) &&
      JSON.stringify(prevProps.manufacturerDataByCustomer[customerId]) ===
        JSON.stringify(nextProps.manufacturerDataByCustomer[customerId]) &&
      JSON.stringify(prevProps.modalityDataByCustomer[customerId]) ===
        JSON.stringify(nextProps.modalityDataByCustomer[customerId])
    );
  },
);

// Modern Styled Components
const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 1.2em;
  font-weight: 600;
  color: ${colors.primary.main};
`;


const AccessTableContainer = styled.div`
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${colors.neutral[200]};
`;

const TableHeader = styled.div`
  display: flex;
  background: ${colors.neutral[50]};
  border-bottom: 2px solid ${colors.neutral[200]};
  font-weight: 600;
  font-size: 12px;
  color: ${colors.neutral[700]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableHeaderCell = styled.div`
  padding: 16px 12px;
  width: ${(props) => props.width};
  text-align: left;
  flex-shrink: 0;
`;

const TableRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${colors.neutral[25]};
  }

  &:last-child {
    border-bottom: none;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    padding: 16px;
    gap: 16px;

    &:not(:last-child) {
      border-bottom: 1px solid ${colors.neutral[200]};
    }
  }
`;

const TableCell = styled.div`
  padding: 16px 12px;
  width: ${(props) => props.width};
  flex-shrink: 0;
  display: flex;
  align-items: ${(props) => (props.alignTop ? "flex-start" : "center")};
  font-size: 14px;

  @media screen and (max-width: 768px) {
    width: 100% !important;
    padding: 8px 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const CustomerLabel = styled.div`
  font-weight: 600;
  color: ${colors.neutral[900]};
  font-size: 14px;
`;


const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  min-height: 60px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const SiteAccessColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.neutral[700]};
`;
